/*
 * Featured product main component
 * Author: Chris
 * CreatedAt: 2021-3-23
 */

import { Collapse, Input } from '@/src/components/Antd';
import {
  EnumPromotionCollapseId,
  EnumPromotionSwitch,
} from '@/src/pages/SalesChannelContentEditor/SalesChannelEditorPromotion/common';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import TextedSwitch from '../../Components/TextedSwitch/TextedSwitch';

import ProductDraggableDisplay from '@/src/constrains/ProductDraggableDisplay/ImgAndSkuShortDisplay';
import { IFeaturedProduct } from '@/src/types/common/SalesChannel';
import { useSalesChannelType } from 'src/constrains/SalesChannelHOC/useSalesChannel';
import GeneralCollapseContent from 'src/constrains/SalesChannelPreviewPageComponents/GeneralCollapseContent';
import GeneralCollapseHeader from 'src/constrains/SalesChannelPreviewPageComponents/GeneralCollapseHeader';
import GeneralCollapseWrapper from 'src/constrains/SalesChannelPreviewPageComponents/GeneralCollapseWrapper';
import ItemWrapper from 'src/constrains/SalesChannelPreviewPageComponents/OverwriteItemWrapper';
import Section from 'src/constrains/SalesChannelPreviewPageComponents/SectionContainer';
import { useSalesChannelWithSisterProductSelectionUuid } from 'src/hooks/useSalesChannelWithSisterProductSelectionUuid';
import { useI18n } from 'src/hooks/useRouterChange';
import { i18nGet } from 'src/tools/tools';
import { ChannelType } from 'src/types/common/SalesChannel';
import {
  ActionType,
  getCollapseOpenStatus,
  onCollapseOpen,
  onProductsChange,
  onSwitchChange,
  onValueChange,
} from '../redux/index';
import css from './FeaturedProduct.less';

interface IProps {
  onChange?: (values) => void;
  isPrimary?: boolean;
  salesChannelUuid: string;
  salesChannelContentUuid: string;
  withoutCollapse?: boolean;
}

const FeaturedProduct = ({
  isPrimary,
  salesChannelUuid,
  salesChannelContentUuid,
  withoutCollapse,
}: IProps) => {
  const i18n = useI18n();
  const getI18n = (str) => i18nGet(i18n, str);
  const type = useSalesChannelType();
  const isMini = type === ChannelType.MiniProgram;
  const history = useHistory();
  const _id = EnumPromotionCollapseId.featuredProducts;
  const _open = useSelector(getCollapseOpenStatus(_id));
  const selectSalesChannelUuid =
    useSalesChannelWithSisterProductSelectionUuid();

  const _d = useDispatch();
  const featuredProducts = useSelector(
    (state: any) => state.salesChannelContent.promotion.featuredProducts,
  ) as IFeaturedProduct;

  const { shopCart, profile, productSearch, fourZeroFour, title, products } =
    featuredProducts;

  const handleInputChange = (value) => {
    _d(
      onValueChange({
        type: ActionType.featureProductTitleChange,
        data: value,
      }),
    );
  };

  const handleChange = (switchType: EnumPromotionSwitch, isSwitchON) => {
    const type = isSwitchON ? ActionType.switchOn : ActionType.switchOff;
    _d(onSwitchChange({ type, data: { switchType } }));
  };

  const handleClickJumpPage = (switchType: EnumPromotionSwitch) => {
    switch (switchType) {
      case EnumPromotionSwitch.shoppingCart: {
        history.push(
          `/salesChannel/editor/content/shoppingCart/${salesChannelUuid}/${salesChannelContentUuid}`,
        );
        break;
      }
      case EnumPromotionSwitch.profile: {
        history.push(
          `/salesChannel/editor/content/profilepage/${salesChannelUuid}/${salesChannelContentUuid}`,
        );
        break;
      }
      case EnumPromotionSwitch.productSearch: {
        history.push(
          `/salesChannel/editor/content/productSearch/${salesChannelUuid}/${salesChannelContentUuid}`,
        );
        break;
      }
      case EnumPromotionSwitch.fourZeroFour: {
        history.push(
          `/salesChannel/editor/content/404/${salesChannelUuid}/${salesChannelContentUuid}`,
        );
        break;
      }
    }
  };

  const _handleProductCarouselItemOnDeleteAll = () => {
    _d(onProductsChange({ type: ActionType.deleteAll, data: [] }));
  };

  const _handleProductCarouselItemOnDelete = (index) => {
    _d(onProductsChange({ type: ActionType.delete, data: index }));
  };

  const _handleProductCarouselItemOnReselect = (products) => {
    _d(onProductsChange({ type: ActionType.reselect, data: products }));
  };

  const _handleProductCarouselItemOnSort = (from, to) => {
    _d(onProductsChange({ type: ActionType.reorder, data: { from, to } }));
  };

  const renderTitle = () => {
    return (
      <GeneralCollapseHeader
        content={getI18n('salesChannelContent.index.featuredProducts')}
      />
    );
  };

  const handleOnCollapseOpen = (status: boolean) => {
    const _g = status ? [_id] : [];
    _d(onCollapseOpen({ idArr: _g }));
  };

  return (
    <GeneralCollapseWrapper>
      <Collapse
        id={_id}
        open={withoutCollapse || _open}
        title={renderTitle()}
        withoutCollapse={withoutCollapse}
        content={
          <GeneralCollapseContent>
            {(isPrimary || withoutCollapse) && (
              <Section bottomBorder>
                <ItemWrapper
                  title={getI18n(
                    'salesChannelContent.index.displayFeatureProductson',
                  )}>
                  <TextedSwitch
                    className={css.switch}
                    title={shopCart.title}
                    defaultChecked={shopCart.checked}
                    onChange={(checked) =>
                      handleChange(EnumPromotionSwitch.shoppingCart, checked)
                    }
                    onClickJumpPage={() =>
                      handleClickJumpPage(EnumPromotionSwitch.shoppingCart)
                    }
                  />

                  <TextedSwitch
                    className={css.switch}
                    title={profile.title}
                    defaultChecked={profile.checked}
                    onChange={(checked) =>
                      handleChange(EnumPromotionSwitch.profile, checked)
                    }
                    onClickJumpPage={() =>
                      handleClickJumpPage(EnumPromotionSwitch.profile)
                    }
                  />

                  <TextedSwitch
                    className={css.switch}
                    title={productSearch.title}
                    defaultChecked={productSearch.checked}
                    onChange={(checked) =>
                      handleChange(EnumPromotionSwitch.productSearch, checked)
                    }
                    onClickJumpPage={() =>
                      handleClickJumpPage(EnumPromotionSwitch.productSearch)
                    }
                  />
                  {!isMini && (
                    <TextedSwitch
                      className={css.switch}
                      title={fourZeroFour.title}
                      defaultChecked={fourZeroFour.checked}
                      onChange={(checked) =>
                        handleChange(EnumPromotionSwitch.fourZeroFour, checked)
                      }
                      onClickJumpPage={() =>
                        handleClickJumpPage(EnumPromotionSwitch.fourZeroFour)
                      }
                    />
                  )}
                </ItemWrapper>
              </Section>
            )}

            <Section bottomBorder>
              <ItemWrapper title={getI18n('general.title')}>
                <Input
                  value={title}
                  onChange={handleInputChange}
                  size='large'
                  maxLength={100}
                />
              </ItemWrapper>
            </Section>
            {(isPrimary || withoutCollapse) && (
              <ProductDraggableDisplay
                displayWarningModalWhenDeleting={true}
                warningModalTitle={getI18n(
                  'salesChannelContent.index.deleteproduct',
                )}
                warningModalContent={getI18n(
                  'salesChannelContent.index.deleteproducttip',
                )}
                onDeleteAll={_handleProductCarouselItemOnDeleteAll}
                onSort={_handleProductCarouselItemOnSort}
                onDelete={_handleProductCarouselItemOnDelete}
                onChange={_handleProductCarouselItemOnReselect}
                droppableId={'testDrop'}
                data={(products || []).map((i: any) => {
                  const { productName } = i;
                  return { ...i, name: productName };
                })}
                salesChannelUuid={selectSalesChannelUuid}
              />
            )}
          </GeneralCollapseContent>
        }
        clickToExpand
        expandOffset={12}
        onClickTrigger={handleOnCollapseOpen}
      />
    </GeneralCollapseWrapper>
  );
};

export default FeaturedProduct;
